import { useState, useEffect, useRef } from 'react';
import { formatCui } from '../../utils/validation';

// CuiInput — câmp CUI cu dropdown de prefix (RO = plătitor TVA / „—" = fără prefix) + câmp numeric.
// Emite prin `onChange` valoarea completă pentru trimitere (ex: „RO12345678" sau „12345678").
// Validarea cifrei de control se face în părinte cu isValidCui (utils/validation.js).
//
// Props: label, value, onChange(string), error, helper, required, disabled.

const CuiInput = ({
  label,
  value,
  onChange,
  error,
  helper,
  required = false,
  disabled = false,
  onBlur,
}) => {
  const [prefix, setPrefix] = useState('RO');
  const [digits, setDigits] = useState('');
  const lastEmitted = useRef(null);

  // Sincronizare cu valoarea externă (mod edit / reset).
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const raw = String(value || '')
      .trim()
      .toUpperCase();
    if (raw.startsWith('RO')) {
      setPrefix('RO');
      setDigits(raw.slice(2).replace(/\D/g, ''));
    } else {
      // Dacă există cifre fără prefix, alegem „—"; dacă e gol, păstrăm default RO.
      const onlyDigits = raw.replace(/\D/g, '');
      if (onlyDigits) setPrefix('');
      setDigits(onlyDigits);
    }
    lastEmitted.current = value;
  }, [value]);

  const emit = (newPrefix, newDigits) => {
    const out = formatCui(newDigits, newPrefix === 'RO');
    lastEmitted.current = out;
    onChange(out);
  };

  const handleDigits = (e) => {
    const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
    setDigits(clean);
    emit(prefix, clean);
  };

  const handlePrefix = (e) => {
    setPrefix(e.target.value);
    emit(e.target.value, digits);
  };

  return (
    <div>
      {label && (
        <label className="input-label">
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div className="flex gap-2">
        <select
          value={prefix}
          onChange={handlePrefix}
          disabled={disabled}
          className={`select shrink-0 w-20 ${error ? 'input-error' : ''}`}
        >
          <option value="RO">RO</option>
          <option value="">—</option>
        </select>
        <input
          type="text"
          inputMode="numeric"
          className={`input flex-1 ${error ? 'input-error' : ''}`}
          value={digits}
          onChange={handleDigits}
          onBlur={onBlur}
          placeholder="ex: 14399840"
          disabled={disabled}
        />
      </div>
      {error && (
        <p className="input-error-text" role="alert">
          {error}
        </p>
      )}
      {helper && !error && <p className="input-helper">{helper}</p>}
    </div>
  );
};

export default CuiInput;
